// ─── Shared ──────────────────────────────────────────────────────────────────

export interface ConfigmanagerConflictResolution {
  path?: string;
  revision?: string;
}

export interface ConfigManagerMergeConflict {
  path?: string;
  base_revision?: string;
  branch_revision?: string;
  target_revision?: string;
}

// ─── Branches ────────────────────────────────────────────────────────────────

export class ConfigManagerBranch {
  name: string;
  description?: string;
  owner?: string;
  updated_user?: string;
  created_at?: number;
  updated_at?: number;
  protected?: boolean;
  environments?: string[];
  linked?: boolean;
  version?: string;

  static create(
    branch: Partial<ConfigManagerBranch> = {},
  ): ConfigManagerBranch {
    return Object.assign(new ConfigManagerBranch(), branch);
  }

  static fork(
    branch: ConfigManagerBranch,
    useBranchName: boolean,
  ): ConfigManagerBranch {
    const branchName = useBranchName ? 'your-branch-name-here' : branch.name;
    return Object.assign(new ConfigManagerBranch(), branch, {
      name: `${branchName}-fork`,
    });
  }
}

export interface ConfigManagerDeleteBranchRequest {
  branch: string;
}

export interface ConfigManagerForkBranchRequest {
  source: string;
  name: string;
  links: boolean;
  description: string;
  project?: string;
}

export interface ConfigManagerMergeResponse {
  branch?: string;
  target_branch?: string;
  source_branch?: string;
  revision?: string;
  common_ancestor_sha?: string;
  conflicts?: ConfigManagerMergeConflict[];
}

export type ConfigManagerDryMergeResponse = ConfigManagerMergeResponse;

// ─── Configs (Files) ─────────────────────────────────────────────────────────

export interface ConfigManagerMetadata {
  username?: string;
  timestamp?: number;
}

export interface ConfigManagerConfig {
  name?: string;
  description?: string;
  owner?: ConfigManagerMetadata;
  last?: ConfigManagerMetadata;
  linked?: boolean;
}

export interface ConfigManagerAddConfigRequest {
  branch: string;
  name: string;
  description?: string;
  data: Record<string, object[]>;
  metadata?: ConfigMetadataLegacy[];
}

export interface ConfigManagerRenameConfigRequest {
  branch: string;
  file: string;
  name: string;
  description: string;
}

export interface ConfigManagerDeleteConfigRequest {
  branch: string;
  file: string;
}

// ─── Sections ────────────────────────────────────────────────────────────────

export interface MetadataColumnLegacy {
  name?: string;
  position?: number;
  validation: string;
}

export interface MetadataCellLegacy {
  __bsid?: string;
  row?: number;
  col: number;
  color?: string;
  comment?: {
    value: string;
  };
}

export interface MetadataExcludedLegacy {
  name: string;
  position: number;
  data: (number | string)[];
}

export interface ConfigMetadataLegacy {
  cells?: MetadataCellLegacy[];
  columns?: MetadataColumnLegacy[];
  excluded?: MetadataExcludedLegacy[];
  sort?: {
    key?: string;
    ascending?: boolean;
  };
}

export interface ConfigLegacyLink {
  id: string;
  gid: number;
}

export interface ConfigLegacy {
  data: object[];
  metadata: ConfigMetadataLegacy;
  link?: ConfigLegacyLink;
}

export interface ConfigManagerAddSectionRequest {
  local: string;
  file: string;
  section_name: string;
  data: object[];
  metadata: ConfigMetadataLegacy;
}

export interface ConfigManagerPutSectionRequest {
  branch: string;
  config: string;
  section: string;
  body: ConfigManagerPutSectionBody;
}

export interface ConfigManagerRenameSectionRequest {
  local: string;
  file: string;
  section: string;
  section_name: string;
}

export interface ConfigManagerDeleteSectionRequest {
  branch: string;
  config: string;
  section: string;
}

export interface ConfigManagerPutSectionBody {
  data?: object[];
  metadata?: ConfigMetadataLegacy;
}

export interface ConfigManagerRenameSectionBody {
  action: 'rename';
  section_name: string;
}

export interface ConfigManagerAddSectionResponse {
  position?: number;
}

// ─── Links (Google Drive) ─────────────────────────────────────────────────────

export interface ConfigManagerConfigLink {
  name: string;
  sections: ConfigManagerSectionLink[];
}

export interface ConfigManagerSectionLink {
  name: string;
  id: string;
  gid: number;
}

export interface ConfigManagerUpdateLinkRequest {
  name: string;
  id: string;
  gid: number;
}
